/**
 * EvaluationMatrixPanel Component
 *
 * Displays detailed evaluation matrix with:
 * - Pillar summary cards with scores by evaluator type
 * - Radar chart (individual vs team)
 * - Insights panel (strengths, development areas, blind spots)
 * - Expandable item-level breakdown
 */
import React from 'react'
import { cn } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card'
import { ChevronDown, ChevronRight, CheckCircle, AlertTriangle, Eye, TrendingUp } from 'lucide-react'
import { RadarChartViz } from './RadarChartViz'
import { useRadarUIStore } from '@/stores/radar-ui-store'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  EvaluationMatrixResult,
  EvaluationMatrixDetailedResult,
  PillarSummary,
  RadarEvaluationItem,
  EvaluationPillar,
  MatrixViewMode,
} from '@/types/radar'

interface EvaluationMatrixPanelProps {
  data: EvaluationMatrixResult | EvaluationMatrixDetailedResult
  className?: string
}

export function EvaluationMatrixPanel({ data, className }: EvaluationMatrixPanelProps) {
  const {
    matrixViewMode,
    setMatrixViewMode,
    expandedPillars,
    togglePillar,
    expandAllPillars,
    collapseAllPillars,
  } = useRadarUIStore()

  // Validate required data structure
  if (!data?.items || !data?.pillarSummaries || !data?.radarChart) {
    return (
      <div className="text-center py-12 text-brand-gray">
        <p>Estrutura de dados de avaliação incompleta.</p>
      </div>
    )
  }

  // Group items by pillar
  const itemsByPillar = React.useMemo(() => {
    const grouped: Record<EvaluationPillar, RadarEvaluationItem[]> = {
      lideranca: [],
      execucao: [],
      colaboracao: [],
      valores: [],
    }
    data.items.forEach((item) => {
      grouped[item.pillar].push(item)
    })
    return grouped
  }, [data.items])

  // Check if detailed data is available
  const hasDetailed = 'detailed' in data
  const detailedData = hasDetailed ? (data as EvaluationMatrixDetailedResult).detailed : null

  return (
    <div className={cn('space-y-6', className)}>
      {/* Cycle info */}
      <div className="flex items-center justify-between">
        <div className="text-brand-gray text-sm">
          Ciclo: <span className="text-white font-medium">{data.cycle?.name ?? 'N/A'}</span>
        </div>
        {data.metadata && (
          <div className="text-brand-gray text-sm">
            Completude: <span className="text-white font-medium">{data.metadata.dataCompleteness}%</span>
          </div>
        )}
      </div>

      {/* Pillar Summary Cards */}
      <Card>
        <CardHeader>
          <CardTitle>Média por Pilar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-gray/20">
                  <th className="text-left py-3 px-2 text-brand-gray font-medium">Pilar</th>
                  <th className="text-center py-3 px-2 text-brand-gray font-medium">Auto</th>
                  <th className="text-center py-3 px-2 text-brand-gray font-medium">Gestor</th>
                  <th className="text-center py-3 px-2 text-brand-gray font-medium">Pares</th>
                  <th className="text-center py-3 px-2 text-white font-medium">Média Final</th>
                </tr>
              </thead>
              <tbody>
                {data.pillarSummaries.map((pillar) => (
                  <tr key={pillar.pillar} className="border-b border-brand-gray/10 hover:bg-brand-dark-light/50">
                    <td className="py-3 px-2 text-white">{pillar.label}</td>
                    <td className="py-3 px-2 text-center">
                      <ScoreCell score={pillar.scores.auto} />
                    </td>
                    <td className="py-3 px-2 text-center">
                      <ScoreCell score={pillar.scores.gestor} />
                    </td>
                    <td className="py-3 px-2 text-center">
                      <ScoreCell score={pillar.scores.pares} />
                    </td>
                    <td className="py-3 px-2 text-center">
                      <ScoreCell score={pillar.scores.media} highlight />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Radar Chart and Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Radar: Individual vs Equipe</CardTitle>
          </CardHeader>
          <CardContent>
            <RadarChartViz
              data={data.radarChart.individual}
              individualAverage={data.radarChart.individualAverage}
              teamAverage={data.radarChart.teamAverage}
            />
          </CardContent>
        </Card>

        {/* Insights */}
        <Card>
          <CardHeader>
            <CardTitle>Insights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Strengths */}
              <InsightSection
                icon={<CheckCircle className="w-4 h-4 text-green-500" />}
                title="Pontos Fortes"
                items={data.radarChart.individual
                  .filter((p) => p.position === 'above')
                  .map((p) => `${p.label} (${p.individual.toFixed(2)})`)}
                emptyText="Nenhum ponto acima da média"
              />

              {/* Above Team */}
              <InsightSection
                icon={<TrendingUp className="w-4 h-4 text-brand-primary" />}
                title="Acima da Equipe"
                items={data.radarChart.individual
                  .filter((p) => p.delta > 0.1)
                  .map((p) => `${p.label}: +${p.delta.toFixed(2)}`)}
                emptyText="Nenhum pilar significativamente acima"
              />

              {/* Blind Spots (if detailed) */}
              {hasDetailed && detailedData?.insights?.blindSpots && detailedData.insights.blindSpots.length > 0 && (
                <InsightSection
                  icon={<Eye className="w-4 h-4 text-yellow-500" />}
                  title="Pontos Cegos"
                  items={detailedData.insights.blindSpots.map(
                    (b) => `${b.itemDescription} (gap: ${b.gap.toFixed(1)})`
                  )}
                  variant="warning"
                />
              )}

              {/* Development Areas */}
              {hasDetailed && detailedData?.insights?.developmentAreas && detailedData.insights.developmentAreas.length > 0 && (
                <InsightSection
                  icon={<AlertTriangle className="w-4 h-4 text-red-500" />}
                  title="Áreas de Desenvolvimento"
                  items={detailedData.insights.developmentAreas.map(
                    (d) => `${d.description} (${d.pillar})`
                  )}
                  variant="error"
                />
              )}

              {/* Summary */}
              <div className="pt-4 border-t border-brand-gray/20 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-gray">Média Individual</span>
                  <span className="text-white font-bold">{data.radarChart.individualAverage.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-gray">Média Equipe</span>
                  <span className="text-brand-gray">{data.radarChart.teamAverage.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-gray">Delta</span>
                  <span className={cn(
                    'font-bold',
                    data.radarChart.individualAverage >= data.radarChart.teamAverage ? 'text-green-500' : 'text-red-500'
                  )}>
                    {data.radarChart.individualAverage >= data.radarChart.teamAverage ? '+' : ''}
                    {(data.radarChart.individualAverage - data.radarChart.teamAverage).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Item-level breakdown */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Detalhamento por Item</CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={matrixViewMode}
              onChange={(e) => setMatrixViewMode(e.target.value as MatrixViewMode)}
              className="text-sm bg-brand-dark-light border border-brand-gray/30 rounded px-2 py-1 text-white"
            >
              <option value="summary">Resumo</option>
              <option value="detailed">Detalhado</option>
            </select>
            <button
              onClick={expandAllPillars}
              className="text-sm text-brand-primary hover:underline"
            >
              Expandir todos
            </button>
            <button
              onClick={collapseAllPillars}
              className="text-sm text-brand-gray hover:underline"
            >
              Recolher
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data.pillarSummaries.map((pillar) => (
              <PillarAccordion
                key={pillar.pillar}
                pillar={pillar}
                items={itemsByPillar[pillar.pillar]}
                isExpanded={expandedPillars.includes(pillar.pillar)}
                onToggle={() => togglePillar(pillar.pillar)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Metadata */}
      {data.evaluatorBreakdown && data.metadata && (
        <div className="text-center text-sm text-brand-gray">
          Avaliadores: Auto{data.evaluatorBreakdown.gestor?.length > 0 && `, ${data.evaluatorBreakdown.gestor.map(g => g.evaluatorName).join(', ')}`}
          {data.evaluatorBreakdown.pares?.length > 0 && `, ${data.evaluatorBreakdown.pares.length} Pares`}
          <br />
          Completude: {data.metadata.dataCompleteness}% | {data.metadata.completedItems} de {data.metadata.totalItems} itens
        </div>
      )}
    </div>
  )
}

/**
 * Score cell with color coding
 */
function ScoreCell({ score, highlight = false }: { score: number | null; highlight?: boolean }) {
  if (score === null) {
    return <span className="text-brand-gray">—</span>
  }

  return (
    <span className={cn(
      highlight ? 'font-bold' : 'font-medium',
      score >= 4.5 ? 'text-green-500' :
      score >= 3.5 ? 'text-brand-primary' :
      score >= 2.5 ? 'text-yellow-500' : 'text-red-500'
    )}>
      {score.toFixed(2)}
    </span>
  )
}

/**
 * Insight section
 */
function InsightSection({
  icon,
  title,
  items,
  emptyText,
  variant = 'default',
}: {
  icon: React.ReactNode
  title: string
  items: string[]
  emptyText?: string
  variant?: 'default' | 'warning' | 'error'
}) {
  const variantClasses = {
    default: 'bg-green-500/5 border-green-500/20',
    warning: 'bg-yellow-500/5 border-yellow-500/20',
    error: 'bg-red-500/5 border-red-500/20',
  }

  if (items.length === 0 && !emptyText) return null

  return (
    <div className={cn('p-3 rounded-lg border', variantClasses[variant])}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-white font-medium text-sm">{title}</span>
      </div>
      {items.length > 0 ? (
        <ul className="space-y-1 text-sm text-brand-gray">
          {items.map((item, i) => (
            <li key={i}>• {item}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-brand-gray italic">{emptyText}</p>
      )}
    </div>
  )
}

/**
 * Pillar accordion
 */
function PillarAccordion({
  pillar,
  items,
  isExpanded,
  onToggle,
}: {
  pillar: PillarSummary
  items: RadarEvaluationItem[]
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="border border-brand-gray/20 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-brand-dark-light/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-brand-gray" />
          ) : (
            <ChevronRight className="w-4 h-4 text-brand-gray" />
          )}
          <span className="text-white font-medium">{pillar.label}</span>
          <span className="text-brand-gray text-sm">({pillar.itemCount} itens)</span>
        </div>
        <span className={cn(
          'font-bold',
          pillar.scores.media >= 4.5 ? 'text-green-500' :
          pillar.scores.media >= 3.5 ? 'text-brand-primary' :
          pillar.scores.media >= 2.5 ? 'text-yellow-500' : 'text-red-500'
        )}>
          {pillar.scores.media.toFixed(2)}
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-brand-gray/20">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-dark-lighter">
                <th className="text-left py-2 px-3 text-brand-gray font-medium">Item</th>
                <th className="text-center py-2 px-3 text-brand-gray font-medium w-16">Auto</th>
                <th className="text-center py-2 px-3 text-brand-gray font-medium w-16">Gestor</th>
                <th className="text-center py-2 px-3 text-brand-gray font-medium w-16">Pares</th>
                <th className="text-center py-2 px-3 text-white font-medium w-16">Média</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.itemId} className="border-t border-brand-gray/10">
                  <td className="py-2 px-3 text-white">{item.description}</td>
                  <td className="py-2 px-3 text-center">
                    <ScoreCell score={item.scores.auto} />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <ScoreCell score={item.scores.gestor} />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <ScoreCell score={item.scores.pares} />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <ScoreCell score={item.scores.media} highlight />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * Loading skeleton
 */
export function EvaluationMatrixSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-36 bg-brand-gray/20" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full bg-brand-gray/20" />
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-[400px] bg-brand-gray/20 rounded-xl" />
        <Skeleton className="h-[400px] bg-brand-gray/20 rounded-xl" />
      </div>
    </div>
  )
}
